import { useState, useEffect } from "react";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { getCurrencyAmount } from "./../../../utils/stringUtils";
import Toaster from "./../../../components/common/Toaster";
import journalService from "../../../services/journalService";
import moment from "moment";

const columns = [
  { label: "DATE" },
  { label: "NO." },
  { label: "NAME" },
  { label: "DEBIT" },
  { label: "CREDIT" },
  { label: "BALANCE" },
];

const GeneralLedger = ({ id }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const { result } =
          await journalService.getAccountGeneralLedgerInfoAsync(id);

        const entries = result.entries.map((item) => {
          item["transaction_date"] = item.transaction_date
            ? moment(item.transaction_date).format("DD-MMM-yyyy")
            : "";

          return item;
        });

        result["items"] = entries;

        if (mounted) {
          setIsLoading(false);
          setData(result);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [id]);

  const handleExport = async () => {
    try {
      const { result } = await journalService.generateGeneralLedgerAsync(id);

      const mediaType =
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,";

      const link = document.createElement("a");
      link.href = mediaType + result.content;
      link.download = result.file_name;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  };

  return (
    <div className="card p-6">
      <div className="flex justify-end mb-2 px-5">
        <button
          className="button button-primary w-44 text-white"
          onClick={() => handleExport()}
        >
          <IoCloudDownloadOutline className="mr-2" /> Export to Excel
        </button>
      </div>
      {data && !isLoading && (
        <div className="p-4">
          <div className="mb-10 text-center">
            <p>Dr. {data.name}</p>
            <span className="font-bold text-sm">General Ledger</span>
          </div>
          <table className="min-w-full table-fixed">
            <thead>
              <tr className="border-t border-b border-black">
                {columns.map((item, index) => (
                  <th
                    key={index}
                    className="font-semibold text-sm py-1 px-4 text-left border-r border-gray-200 last:border-r-0"
                  >
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items &&
                data.items.map((item, index) => {
                  if (item.is_heading) {
                    return (
                      <tr key={index} className="text-sm">
                        <td colSpan={5} className="px-4 py-2 font-semibold">
                          {item.label}
                        </td>
                      </tr>
                    );
                  }

                  if (item.is_total) {
                    return (
                      <tr key={index} className="text-sm border-t">
                        <td className="px-4 font-semibold" colSpan={3}>
                          {item.label}
                        </td>
                        <td className="px-4 py-2 font-semibold text-right">
                          PHP {getCurrencyAmount(item.debit)}
                        </td>
                        <td className="px-4 py-2 font-semibold text-right">
                          PHP {getCurrencyAmount(item.credit)}
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={index} className="text-sm">
                      <td className="px-6">{item.transaction_date}</td>
                      <td className="px-4">{item.transaction_number}</td>
                      <td className="px-4">{item.name}</td>
                      <td className="px-4 text-right">
                        {item.debit && getCurrencyAmount(item.debit)}
                      </td>
                      <td className="px-4 text-right">
                        {item.credit && getCurrencyAmount(item.credit)}
                      </td>
                      <td className="px-4 text-right">
                        {item.balance && getCurrencyAmount(item.balance)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GeneralLedger;
